import { api_configuration } from "common";
import { addMessage } from "../front/messages";
import openViewOnNode from "../front/openViewOnNode";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { main } from "../state/ClientState";
import * as hash from "../widgets/dom/hash";
import { FormatString, TranslationKey } from "./translate";
import gui_definition_types = api_configuration.libraries.gui_definition_lib.types;
import { entryExists } from "../state 2 interface/entryExists";

function asyncWarning(title: TranslationKey, body: TranslationKey | FormatString) {
	// warn async because the message container can be uninitialized at application start.
	setTimeout(function () {
		addMessage("warning", title, { body: body });
	}, 0);
}

function idPathOfView(
	step: gui_definition_types.views.alan_api.Cgui_node_type_path_step,
	keys: string[],
	builder: IdPathBuilder
): IdPathBuilder | undefined {
	switch (step.properties.has_steps.state.name) {
		case "no": {
			return builder;
		}
		case "yes": {
			const yes = step.properties.has_steps.state.node;
			const tail = yes.properties.tail;
			const attribute = yes.properties.attribute;
			switch (yes.properties.type.state.name) {
				case "node": {
					switch (attribute.ref.properties.type.state.name) {
						case "action":
							return undefined;
						case "command":
							return undefined;
						case "property": {
							const property = attribute.ref.properties.type.state.node;
							switch (property.properties.type.state.name) {
								case "collection": {
									const key = keys[0];
									if (key === undefined) {
										return undefined;
									}
									return idPathOfView(tail, keys.slice(1), builder.collectionEntry(attribute.entry, key));
								}
								case "group": {
									return idPathOfView(tail, keys, builder.group(attribute.entry));
								}
								case "file": {
									return undefined;
								}
								case "number": {
									return undefined;
								}
								case "state group": {
									return undefined;
								}
								case "text": {
									return undefined;
								}
								default:
									return unexpectedState(property.properties.type.state);
							}
						}
						case "reference set":
							return undefined;
						default:
							return unexpectedState(attribute.ref.properties.type.state);
					}
				}
				case "state": {
					const $ = yes.properties.type.state.node;
					return idPathOfView(tail, keys, builder.state(attribute.entry, $.properties.state.entry));
				}
				default:
					return unexpectedState(yes.properties.type.state);
			}
		}
		default:
			unexpectedState(step.properties.has_steps.state);
	}
}

export default async function (target_window: gui_definition_types.settings.alan_api.Cwindows): Promise<void> {
	let deeplink: { views: string; view: string; context: string } | undefined;
	try {
		deeplink = hash.getDeepLink();
	} catch (e) {
		asyncWarning("Could not open view.", { phrase: "The URL might be wrong. {0}", vars: [e] });
	}
	if (deeplink !== undefined) {
		const views_name = deeplink.views;
		const view_name = deeplink.view;
		const context = deeplink.context;

		let keys: string[] = [];
		IdPathBuilder.parse(
			context,
			(builder) => {
				// Parse the context in the old deeplink format and convert it to the new format.
				const app_id = api_configuration.types.application_identifier.alan_api.Capplication_identifier.create({
					path: builder.build(),
				}).properties.path;
				const walkId = (
					id: api_configuration.types.application_identifier.alan_api.Cid_path,
					steps: string[]
				): string[] => {
					switch (id.properties.has_steps.state.name) {
						case "no": {
							return steps;
						}
						case "yes": {
							const has_step = id.properties.has_steps.state.node;
							switch (has_step.properties.type.state.name) {
								case "collection entry": {
									const $ = has_step.properties.type.state.node;
									steps.push($.properties.id);
									break;
								}
								case "group":
									break;
								case "state":
									break;
								default:
									unexpectedState(has_step.properties.type.state);
							}
							return walkId(has_step.properties.tail, steps);
						}
						default:
							unexpectedState(id.properties.has_steps.state);
					}
				};
				keys = walkId(app_id, []);
				// Rewrite the hash using the new format.
				hash.deepLinkTo(views_name, view_name, builder);
			},
			() => {
				keys = hash.parseKeys(context);
			}
		);

		const c_views = main.input.views[views_name];
		const c_view = c_views?.properties.views.get(view_name);
		if (c_view !== undefined) {
			const id = idPathOfView(c_view.properties.node_type.properties.steps, keys, IdPathBuilder.create());
			if (id !== undefined && (await entryExists(id))) {
				openViewOnNode(id, c_view, target_window);
			} else {
				asyncWarning("Could not open view.", { phrase: "Invalid context {0}", vars: [keys.join("/")] });
			}
		} else {
			asyncWarning("Could not open view.", {
				phrase: "The view does not exist {0} {1}",
				vars: [views_name, view_name],
			});
		}
	}
}
